import React, { useState } from "react";
import { SlidersHorizontal, ChevronDown, X } from "lucide-react";
import DateRangeFilter from "./DateRangeFilter";

const Select = ({ label, value, onChange, options }) => (
  <label className="flex flex-col gap-1 min-w-[9rem]">
    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
    >
      <option value="">הכל</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </label>
);

const EMPTY_FILTERS = {
  subject: "", teacherId: "", studentId: "", lessonType: "",
  requestStatus: "", paymentStatus: "", lessonStatus: "", meetingType: "", hasRecording: "",
};

/** filters: subset of EMPTY_FILTERS keys. Options lists would normally come
 * from the API (teachers/subjects lists) — passed in as props with sane
 * mock defaults so this component works standalone.
 *
 * dateRange/onDateRangeChange are optional: pass them to show the date
 * range picker in the always-visible header row (it's a primary filter,
 * so it isn't hidden behind the "מסננים מתקדמים" toggle like the rest). */
const DashboardFilters = ({
  filters, onChange,
  subjectOptions = [], teacherOptions = [],
  dateRange, onDateRangeChange,
}) => {
  const [open, setOpen] = useState(false);
  const activeCount = Object.values(filters).filter(Boolean).length;

  const set = (key, val) => onChange({ ...filters, [key]: val });
  const clearAll = () => onChange(EMPTY_FILTERS);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-sm font-bold text-gray-800"
        >
          <SlidersHorizontal className="w-4 h-4 text-indigo-600" />
          מסננים מתקדמים
          {activeCount > 0 && (
            <span className="text-[10px] font-bold bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5">
              {activeCount} פעילים
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {dateRange && onDateRangeChange && (
          <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />
        )}
      </div>

      {open && (
        <div className="px-4 sm:px-5 pb-4 flex flex-wrap gap-3 items-end border-t border-gray-100 pt-4">
          <Select label="מקצוע" value={filters.subject} onChange={(v) => set("subject", v)} options={subjectOptions} />
          <Select label="מורה" value={filters.teacherId} onChange={(v) => set("teacherId", v)} options={teacherOptions} />
          <Select label="סוג שיעור" value={filters.lessonType} onChange={(v) => set("lessonType", v)} options={[
            { value: "private", label: "פרטי" }, { value: "group", label: "קבוצתי" },
          ]} />
          <Select label="סטטוס בקשה" value={filters.requestStatus} onChange={(v) => set("requestStatus", v)} options={[
            { value: "NO_RESPONSE", label: "ממתין לתגובה" }, { value: "APPROVED", label: "אושר" },
            { value: "PAID", label: "שולם" }, { value: "CANCELLED", label: "בוטל" },
          ]} />
          <Select label="סטטוס תשלום" value={filters.paymentStatus} onChange={(v) => set("paymentStatus", v)} options={[
            { value: "completed", label: "הושלם" }, { value: "failed", label: "נכשל" }, { value: "refunded", label: "זוכה" },
          ]} />
          <Select label="סטטוס שיעור" value={filters.lessonStatus} onChange={(v) => set("lessonStatus", v)} options={[
            { value: "SCHEDULED", label: "מתוזמן" }, { value: "COMPLETED", label: "הושלם" },
            { value: "CANCELLED", label: "בוטל" }, { value: "NO_SHOW", label: "לא הגיע" },
          ]} />
          <Select label="סוג פגישה" value={filters.meetingType} onChange={(v) => set("meetingType", v)} options={[
            { value: "ZOOM", label: "Zoom" }, { value: "MEET", label: "Google Meet" },
          ]} />
          <Select label="הקלטה" value={filters.hasRecording} onChange={(v) => set("hasRecording", v)} options={[
            { value: "yes", label: "עם הקלטה" }, { value: "no", label: "ללא הקלטה" },
          ]} />

          {activeCount > 0 && (
            <button onClick={clearAll} className="flex items-center gap-1 text-xs font-bold text-rose-600 hover:text-rose-700 pb-1.5">
              <X className="w-3.5 h-3.5" /> נקה מסננים
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export { EMPTY_FILTERS };
export default DashboardFilters;